import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { palette, Icon } from '@m12s/component-library';
import { useTranslation } from 'react-i18next';

import {
  millisSince,
  getUnitDurationFromString,
  formatDuration,
} from 'lib/utils/date';
import { DurationTicker } from 'components/DurationTicker';
import { TickerContext } from 'components/TickerProvider';

import { exclamationCircleLight } from 'lib/icons';
import {
  ReminderContainer,
  ReminderIconTextContainer,
  ReminderText,
  DataContainer,
  GroupDataContainer,
  DataRow,
  EachRowData,
  DataColumn,
  EachColumnData,
  DataTitle,
  DisplayData,
  SetupPerformanceData,
  GoodPartsInput,
  PlusMinusButton,
  SetupPerformanceTitle,
  PartsInputGroup,
} from './styled';

const SetupActivityReview = ({
  setupRejectCount,
  adjustedSetupStartDate,
  setupEndDateOverride,
  expectedSetupDurationStr,
  expectedSetupDuration,
  dispatch,
  formState,
}) => {
  useContext(TickerContext);
  const totalSetupTime = adjustedSetupStartDate
    ? millisSince(adjustedSetupStartDate)
    : 0;
  const setupPerformanceSeconds =
    (getUnitDurationFromString(expectedSetupDuration) - totalSetupTime) / 1000;

  const setupPerformance = formatDuration(Math.abs(setupPerformanceSeconds));

  const { t } = useTranslation();

  return (
    <>
      <ReminderContainer>
        <ReminderIconTextContainer>
          <Icon
            size="2rem"
            icon={exclamationCircleLight}
            color={palette.Purple600}
            style={{ marginRight: '1rem' }}
          />
          <ReminderText>
            {t(
              'Adjust the part count below as necessary to remove any empty cycles or any parts that were counted incorrectly. Any rejected parts have already been removed from the part count.'
            )}
          </ReminderText>
        </ReminderIconTextContainer>
      </ReminderContainer>

      <DataContainer>
        <GroupDataContainer>
          <DataRow>
            <EachRowData>
              <DataTitle heavyFontWeight>{t('Elapsed')}</DataTitle>
              <DisplayData>
                {adjustedSetupStartDate ? (
                  <DurationTicker
                    startDate={adjustedSetupStartDate}
                    endDateOverride={setupEndDateOverride}
                  />
                ) : (
                  '—'
                )}
              </DisplayData>
            </EachRowData>

            <>
              <EachRowData>
                <DataTitle heavyFontWeight>{t('Expected')}</DataTitle>
                <DisplayData>
                  {!expectedSetupDurationStr ? 'N/A' : expectedSetupDurationStr}
                </DisplayData>
              </EachRowData>
              <EachRowData>
                <SetupPerformanceTitle heavyFontWeight>
                  {/* expected - actual > 0, good thing: faster than expected */}
                  {setupPerformanceSeconds > 0
                    ? t('Below Expected')
                    : t('Above Expected')}
                </SetupPerformanceTitle>
                <SetupPerformanceData
                  setupPerformancePositive={setupPerformanceSeconds > 0}
                >
                  {setupPerformance}
                </SetupPerformanceData>
              </EachRowData>
            </>

            <EachRowData>
              <DataTitle heavyFontWeight>{t('Rejected Parts')}</DataTitle>
              <DisplayData>{setupRejectCount}</DisplayData>
            </EachRowData>
          </DataRow>

          <DataColumn>
            <EachColumnData>
              <DataTitle heavyFontWeight>{t('Good Parts')}</DataTitle>
              <DisplayData>
                <PartsInputGroup>
                  <GoodPartsInput
                    type="number"
                    value={formState?.parts}
                    min={0}
                    onChange={(e) => {
                      const parsed = Number.parseInt(e.target.value, 10);
                      const value = Number.isNaN(parsed)
                        ? ''
                        : Math.abs(parsed);
                      return dispatch({
                        type: 'SET_PARTS',
                        parts: value,
                      });
                    }}
                    onBlur={() => {
                      if (!formState?.parts) {
                        dispatch({
                          type: 'SET_PARTS',
                          parts: 0,
                        });
                      }
                    }}
                    onFocus={() => {
                      if (!formState?.parts) {
                        dispatch({
                          type: 'SET_PARTS',
                          parts: '',
                        });
                      }
                    }}
                    onKeyUp={(event) => {
                      if (event.keyCode === 13) {
                        event.preventDefault();
                        event.target.blur();
                      }
                    }}
                  />
                  <PlusMinusButton
                    id="decrement-setup-parts"
                    rounded
                    onClick={() => {
                      const current = Number(formState?.parts) || 0;
                      return dispatch({
                        type: 'SET_PARTS',
                        parts: Math.max(current - 1, 0),
                      });
                    }}
                  >
                    {t('-')}
                  </PlusMinusButton>
                  <PlusMinusButton
                    id="increment-setup-parts"
                    rounded
                    onClick={() => {
                      const current = Number(formState?.parts) || 0;
                      return dispatch({
                        type: 'SET_PARTS',
                        parts: current + 1,
                      });
                    }}
                  >
                    {t('+')}
                  </PlusMinusButton>
                </PartsInputGroup>
              </DisplayData>
            </EachColumnData>
          </DataColumn>
        </GroupDataContainer>
      </DataContainer>
    </>
  );
};

SetupActivityReview.propTypes = {
  setupRejectCount: PropTypes.number,
  adjustedSetupStartDate: PropTypes.string,
  setupEndDateOverride: PropTypes.string,
  expectedSetupDurationStr: PropTypes.string,
  expectedSetupDuration: PropTypes.string,
};

export { SetupActivityReview };
